import {
    Document,
    Page,
    View
} from '@react-pdf/renderer';

import styles from './styles/styles';
import Header from './components/header';
import Species from './components/species';
import Biomass from './components/biomass';
import Status from './components/status';
import StandAndHarvest from './components/standAndHarvest';
import Nature from './components/nature';
import SpecialRequirements from './components/specialRequirements';
import Files from './components/files';
import Cascade from './components/cascade';
import ScreeningDate from './components/screeningDate';
import Footer from '../deliveryNotes/components/footer';

interface ScreeningDocumentProps {
    printLogo: string | undefined;
    headerLabels: {
        title: string;
        date: string;
        estate: string;
        company: string;
        projectTitle: string;
        taskTitle: string;
        // taskID: string;
        responsible: string;
        note: string;
    };
    headerData: {
        date: string;
        estate: string;
        company: string;
        projectTitle: string;
        taskTitle: string;
        // taskID: string;
        responsible: string;
        note: string;
    };

    speciesLabels: {
        header: string;
        species: string;
    };
    speciesData: {
        species: string | null | undefined;
    };

    biomassLabels: {
        header: string;
        category: string;
        type: string;
        riscAssessment: string;
        certificationNumbers: string;
    };
    biomassData: {
        category: string | null | undefined;
        type: string | null | undefined;
        riscAssessment: string | undefined;
        fscCertificationNumber: string | null | undefined;
        pefcCertificationNumber: string | null | undefined;
        legacyCertificationNumber: string | null | undefined;
    };

    statusLabels: {
        header: string;
        protectedForest: string;
        greenOperationsPlan: string;
        constraintsMap: string;
    };
    statusData: {
        protectedForest: string | null | undefined;
        greenOperationsPlan: string | null | undefined;
        constraintsMap: string | null | undefined;
    };

    standAndHarvestLabels: {
        header: string;
        undergrowthInOldForest: string;
        unevenAgedFoliage: string;
        activity: string;
        regeneration: string;
        expertReview: string;
        appendix16Value: string;
        appendix25Value: string;
        forestryEvaluation: string;
        forestryEvaluationAssessment: string;
    };
    standAndHarvestData: {
        undergrowthInOldForest: string | null | undefined;
        unevenAgedFoliage: string | null | undefined;
        activity: string | null | undefined;
        regeneration: string | null | undefined;
        expertReview: string | null | undefined;
        appendix16Value: string | null | undefined;
        appendix25Value: string | null | undefined;
        forestryEvaluation: string | null | undefined;
        forestryEvaluationAssessment: string | null | undefined;
        category: string | undefined;
    };

    natureLabels: {
        header: string;
        hnvForestAbove9: string;
        hnvAbove5: string;
        hnvValueForest: string;
        hnvValue: string;
        nbl: string;
        heathland: string;
        dispensation: string;
        natura2000: string;
        subjectToNotification: string;
        heritageSite: string;
        redlistSpecies: string;
        skl25: string;
        natureValues: string;
    };
    natureData: {
        hnvForestAbove9: string | null | undefined;
        hnvAbove5: string | null | undefined;
        hnvValue: string | null | undefined;
        nbl: string | null | undefined;
        heathland: string | null | undefined;
        dispensation: string | null | undefined;
        natura2000: string | null | undefined;
        subjectToNotification: string | null | undefined;
        heritageSite: string | null | undefined;
        redlistSpecies: string | null | undefined;
        skl25: string | null | undefined;
        natureValues: string | null | undefined;
    };

    specialRequirementsLabels: {
        header: string;
        customerReq: string;
    };
    specialRequirementsData: {
        customerReq: string | null | undefined;
    };

    filesLabels: {
        header: string;
        files: string;
    };
    filesData: {
        files: string | null | undefined;
    };

    cascadeLabels: {
        header: string;
        subHeader: string;
    };
    cascadeData: {
        jusitfication: string | null | undefined;
    };

    screeningDateLabels: {
        header: string;
        subHeader: string;
    };
    screeningDateData: {
        date: string | null | undefined;
    };

    screeningFooter: string;
}

const hasDisplayableData = (data: Record<string, any>) =>
    Object.values(data).some((val) => val !== undefined);

const ScreeningDocument = ({
    printLogo,
    headerLabels,
    headerData,

    speciesLabels,
    speciesData,

    biomassLabels,
    biomassData,

    statusLabels,
    statusData,

    standAndHarvestLabels,
    standAndHarvestData,

    natureLabels,
    natureData,

    specialRequirementsLabels,
    specialRequirementsData,

    filesLabels,
    filesData,

    cascadeLabels,
    cascadeData,

    screeningDateLabels,
    screeningDateData,

    screeningFooter
}: ScreeningDocumentProps) => {
    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View>
                    <Header
                        labels={headerLabels}
                        data={headerData}
                        logoSrc={printLogo}
                    />

                    {hasDisplayableData(speciesData) && <>
                        <View style={styles.spacer} /><View style={styles.line} /><View style={styles.spacer} />

                        <Species
                            labels={speciesLabels}
                            data={speciesData}
                        />
                    </>}

                    {hasDisplayableData(biomassData) && <>
                        <View style={styles.spacer} /><View style={styles.line} /><View style={styles.spacer} />

                        <Biomass
                            labels={biomassLabels}
                            data={biomassData}
                        />
                    </>}

                    {hasDisplayableData(statusData) && <>
                        <View style={styles.spacer} /><View style={styles.line} /><View style={styles.spacer} />

                        <Status
                            labels={statusLabels}
                            data={statusData}
                        />
                    </>}

                    {hasDisplayableData(
                        Object.fromEntries(
                            Object.entries(standAndHarvestData).filter(([key]) => key !== "category")
                        )
                    ) && <>
                            <View style={styles.spacer} /><View style={styles.line} /><View style={styles.spacer} />

                            <StandAndHarvest
                                labels={standAndHarvestLabels}
                                data={standAndHarvestData}
                            />
                        </>}

                    {hasDisplayableData(natureData) && <>
                        <View style={styles.spacer} /><View style={styles.line} /><View style={styles.spacer} />

                        <Nature
                            labels={natureLabels}
                            data={natureData}
                        />
                    </>}

                    {hasDisplayableData(specialRequirementsData) && <>
                        <View style={styles.spacer} /><View style={styles.line} /><View style={styles.spacer} />

                        <SpecialRequirements
                            labels={specialRequirementsLabels}
                            data={specialRequirementsData}
                        />
                    </>}

                    {hasDisplayableData(filesData) && <>
                        <View style={styles.spacer} /><View style={styles.line} /><View style={styles.spacer} />

                        <Files
                            labels={filesLabels}
                            data={filesData}
                        />
                    </>}

                    {/* ny */}
                    {hasDisplayableData(cascadeData) && <>
                        <View style={styles.spacer} /><View style={styles.line} /><View style={styles.spacer} />

                        <Cascade
                            labels={cascadeLabels}
                            data={cascadeData}
                        />
                    </>}

                    {hasDisplayableData(screeningDateData) && <>
                        <View style={styles.spacer} /><View style={styles.line} /><View style={styles.spacer} />

                        <ScreeningDate
                            labels={screeningDateLabels}
                            data={screeningDateData}
                        />
                    </>}
                </View>

                <Footer footerText={screeningFooter} />
            </Page>
        </Document>
    );
}

export default ScreeningDocument;
